import React, { useState, useEffect, useRef } from 'react';
import { baseUrl } from '../../shared';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const EQUIPMENT_TYPES = {
  CALIBRATOR: 'Calibrator',
  TRANSDUCER: 'Transducer',
  DISPLAY_UNIT: 'Display Unit',
  OTHER: 'Other',
};

const BOILERPLATE_KEYS = [
  { key: 'boilerplate_HYD_TW',      label: 'Hydraulic Torque Wrench' },
  { key: 'boilerplate_BATT_TW',     label: 'Battery Torque Wrench' },
  { key: 'boilerplate_HYD_PRESSURE',label: 'Hydraulic Pressure/Torque Factor' },
  { key: 'boilerplate_PNU',         label: 'Pneumatic Calibration' },
  { key: 'boilerplate_GAUGE',       label: 'Gauge Calibration' },
  { key: 'boilerplate_DIAL',        label: 'Dial Indicator Calibration' },
  { key: 'boilerplate_HOSE_MDG41',  label: 'Hydraulic Hose (MDG41)' },
  { key: 'boilerplate_HOSE_JACK',   label: 'Hose/Jack Test' },
];

// ── Equipment modal ──────────────────────────────────────────────────────────

const EMPTY_EQUIP = { name: '', model: '', serialNo: '', equipmentType: 'CALIBRATOR', isPortableTransducer: false, isActive: true };

function EquipmentModal({ open, onClose, equipment, onSaved }) {
  const [form, setForm] = useState(EMPTY_EQUIP);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(equipment
        ? { name: equipment.name || '', model: equipment.model || '', serialNo: equipment.serialNo || '',
            equipmentType: equipment.equipmentType || 'CALIBRATOR',
            isPortableTransducer: !!equipment.isPortableTransducer, isActive: !!equipment.isActive }
        : EMPTY_EQUIP);
    }
  }, [open, equipment]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setCheck = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.checked }));

  const handleSave = async () => {
    if (!form.name || !form.equipmentType) return;
    setSaving(true);
    try {
      const url = equipment
        ? baseUrl + 'workshop/settings/equipment/' + equipment.id
        : baseUrl + 'workshop/settings/equipment';
      const method = equipment ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{equipment ? 'Edit Equipment' : 'Add Test Equipment'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField size="small" fullWidth label="Name *" value={form.name} onChange={set('name')} />
          <FormControl fullWidth size="small">
            <InputLabel>Type *</InputLabel>
            <Select value={form.equipmentType} label="Type *" onChange={set('equipmentType')}>
              {Object.entries(EQUIPMENT_TYPES).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" fullWidth label="Model" value={form.model} onChange={set('model')} />
          <TextField size="small" fullWidth label="Serial No" value={form.serialNo} onChange={set('serialNo')} />
          <FormControlLabel
            control={<Checkbox checked={form.isPortableTransducer} onChange={setCheck('isPortableTransducer')} />}
            label="Portable transducer (applies offset corrections during mobile calibrations)"
          />
          {equipment && (
            <FormControlLabel
              control={<Checkbox checked={form.isActive} onChange={setCheck('isActive')} />}
              label="Active"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
          {saving ? <CircularProgress size={20} /> : equipment ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Cal record modal ─────────────────────────────────────────────────────────

const EMPTY_CAL = {
  certNo: '', certDate: '', certDueDate: '', calibratedBy: '',
  traceability: '', uncertaintyPct: '', maxErrorPct: '', notes: '',
};

const DEFAULT_OFFSETS = [1000,2000,3000,4000,5000,6000,7000,8000,9000,10000].map(psi => ({ psi, offsetNm: '' }));

function CalRecordModal({ open, onClose, equipmentId, isPortableTransducer, onSaved }) {
  const [form, setForm] = useState(EMPTY_CAL);
  const [offsets, setOffsets] = useState(DEFAULT_OFFSETS.map(r => ({ ...r })));
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (open) { setForm(EMPTY_CAL); setOffsets(DEFAULT_OFFSETS.map(r => ({ ...r }))); setSelectedFile(null); setUploadProgress(0); }
  }, [open]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setOffset = (i) => (e) => setOffsets(prev => prev.map((r, idx) => idx === i ? { ...r, offsetNm: e.target.value } : r));

  const handleSave = () => {
    setSaving(true);
    setUploading(!!selectedFile);
    setUploadProgress(0);

    const correctionData = isPortableTransducer
      ? offsets.filter(r => r.offsetNm !== '' && !isNaN(parseFloat(r.offsetNm))).map(r => ({ psi: r.psi, offsetNm: parseFloat(r.offsetNm) }))
      : null;

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v !== '') formData.append(k, v); });
    if (correctionData && correctionData.length > 0) formData.append('correctionData', JSON.stringify(correctionData));
    if (selectedFile) formData.append('myFile', selectedFile);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', baseUrl + 'workshop/settings/equipment/' + equipmentId + '/calibrations');
    xhr.setRequestHeader('username', localStorage.getItem('username'));
    xhr.setRequestHeader('bToken', localStorage.getItem('bToken'));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadProgress(Math.round((e.loaded * 100) / e.total));
    };

    xhr.onload = () => {
      setSaving(false);
      setUploading(false);
      onSaved();
    };
    xhr.onerror = () => { setSaving(false); setUploading(false); };
    xhr.send(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Calibration Record</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField size="small" fullWidth label="Cert No" value={form.certNo} onChange={set('certNo')} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" fullWidth label="Cert Date" type="date" value={form.certDate} onChange={set('certDate')} InputLabelProps={{ shrink: true }} />
            <TextField size="small" fullWidth label="Cert Due Date" type="date" value={form.certDueDate} onChange={set('certDueDate')} InputLabelProps={{ shrink: true }} />
          </Box>
          <TextField size="small" fullWidth label="Calibrated By" value={form.calibratedBy} onChange={set('calibratedBy')} />
          <TextField size="small" fullWidth label="Traceability (e.g. NMI(Australia) NATA Lab No.3800)" value={form.traceability} onChange={set('traceability')} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" fullWidth label="Uncertainty %" type="number" value={form.uncertaintyPct} onChange={set('uncertaintyPct')} />
            <TextField size="small" fullWidth label="Max Error %" type="number" value={form.maxErrorPct} onChange={set('maxErrorPct')} />
          </Box>
          <TextField size="small" fullWidth multiline rows={2} label="Notes" value={form.notes} onChange={set('notes')} />

          {isPortableTransducer && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 10 }}>
                Transducer Correction Offsets (NM added to bench reading)
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1 }} />
              <Alert severity="info" sx={{ mb: 1, fontSize: 11 }}>
                Enter the NM offset for each PSI point — the difference between this transducer and the calibration bench at each pressure. Applied automatically during mobile calibrations.
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {offsets.map((row, i) => (
                  <TextField
                    key={row.psi}
                    size="small"
                    label={`${row.psi} PSI offset (NM)`}
                    type="number"
                    value={row.offsetNm}
                    onChange={setOffset(i)}
                    InputProps={{ inputProps: { step: 0.1 } }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box>
            <input ref={fileRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={e => setSelectedFile(e.target.files[0] || null)} />
            <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} onClick={() => fileRef.current.click()}>
              {selectedFile ? selectedFile.name : 'Attach Cert PDF (optional)'}
            </Button>
          </Box>
          {uploading && (
            <Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary">Uploading... {uploadProgress}%</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Equipment row with expandable cal history ─────────────────────────────────

function EquipmentRow({ equipment, onEdit, onDeactivate, onCalAdded }) {
  const [expanded, setExpanded] = useState(false);
  const [cals, setCals] = useState([]);
  const [calsLoading, setCalsLoading] = useState(false);
  const [calModalOpen, setCalModalOpen] = useState(false);

  const isOverdue = equipment.latestCertDueDate && dayjs(equipment.latestCertDueDate).isBefore(dayjs(), 'day');
  const isDueSoon = !isOverdue && equipment.latestCertDueDate &&
    dayjs(equipment.latestCertDueDate).isBefore(dayjs().add(30, 'day'), 'day');

  const loadCals = () => {
    setCalsLoading(true);
    fetch(baseUrl + 'workshop/settings/equipment/' + equipment.id + '/calibrations', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setCals(Array.isArray(d) ? d : []); setCalsLoading(false); })
      .catch(() => setCalsLoading(false));
  };

  const handleExpand = () => {
    if (!expanded) loadCals();
    setExpanded(e => !e);
  };

  const handleDeleteCal = async (calId) => {
    if (!window.confirm('Delete this calibration record?')) return;
    await fetch(baseUrl + 'workshop/settings/equipment/' + equipment.id + '/calibrations/' + calId, {
      method: 'DELETE', headers: authHeaders(),
    });
    loadCals();
    onCalAdded();
  };

  return (
    <>
      <TableRow sx={{ bgcolor: !equipment.isActive ? '#fafafa' : undefined }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isOverdue && (
              <Tooltip title="Calibration overdue">
                <WarningAmberIcon fontSize="small" color="error" />
              </Tooltip>
            )}
            {isDueSoon && !isOverdue && (
              <Tooltip title="Calibration due within 30 days">
                <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />
              </Tooltip>
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{equipment.name}</Typography>
              {equipment.serialNo && (
                <Typography variant="caption" color="text.secondary">S/N: {equipment.serialNo}</Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip label={EQUIPMENT_TYPES[equipment.equipmentType] || equipment.equipmentType} size="small" variant="outlined" />
          {!!equipment.isPortableTransducer && (
            <Chip label="Portable" size="small" sx={{ ml: 0.5, bgcolor: '#e3f2fd' }} />
          )}
        </TableCell>
        <TableCell>{equipment.model || '—'}</TableCell>
        <TableCell>
          {equipment.latestCertNo
            ? <Box>
                <Typography variant="body2">{equipment.latestCertNo}</Typography>
                <Typography variant="caption" color={isOverdue ? 'error' : 'text.secondary'}>
                  Due: {equipment.latestCertDueDate ? dayjs(equipment.latestCertDueDate).format('DD/MM/YYYY') : '—'}
                </Typography>
              </Box>
            : <Typography variant="body2" color="text.secondary">No calibration on file</Typography>
          }
        </TableCell>
        <TableCell>
          <Chip
            label={equipment.isActive ? 'Active' : 'Inactive'}
            size="small"
            sx={{ bgcolor: equipment.isActive ? '#e8f5e9' : '#f5f5f5', color: equipment.isActive ? '#2e7d32' : '#757575' }}
          />
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Add calibration record">
            <IconButton size="small" onClick={() => setCalModalOpen(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(equipment)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {equipment.isActive && (
            <Tooltip title="Deactivate">
              <IconButton size="small" onClick={() => onDeactivate(equipment.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={expanded ? 'Hide history' : 'View cal history'}>
            <IconButton size="small" onClick={handleExpand}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Calibration history expansion */}
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                Calibration History
              </Typography>
              {calsLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
              {!calsLoading && cals.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No calibration records.</Typography>
              )}
              {!calsLoading && cals.length > 0 && (
                <Table size="small" sx={{ mt: 0.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5 }}>Cert No</TableCell>
                      <TableCell sx={{ py: 0.5 }}>Date</TableCell>
                      <TableCell sx={{ py: 0.5 }}>Due</TableCell>
                      <TableCell sx={{ py: 0.5 }}>Calibrated By</TableCell>
                      <TableCell sx={{ py: 0.5 }}>Uncertainty</TableCell>
                      {!!equipment.isPortableTransducer && <TableCell sx={{ py: 0.5 }}>Corrections</TableCell>}
                      <TableCell sx={{ py: 0.5 }}>Cert PDF</TableCell>
                      <TableCell sx={{ py: 0.5 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cals.map(cal => (
                      <TableRow key={cal.id}>
                        <TableCell sx={{ py: 0.5 }}>{cal.certNo || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{cal.certDate ? dayjs(cal.certDate).format('DD/MM/YYYY') : '—'}</TableCell>
                        <TableCell sx={{ py: 0.5, color: cal.certDueDate && dayjs(cal.certDueDate).isBefore(dayjs(), 'day') ? 'error.main' : undefined }}>
                          {cal.certDueDate ? dayjs(cal.certDueDate).format('DD/MM/YYYY') : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>{cal.calibratedBy || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {cal.uncertaintyPct != null ? `${cal.uncertaintyPct}%` : '—'}
                          {cal.maxErrorPct != null ? ` / ${cal.maxErrorPct}%` : ''}
                        </TableCell>
                        {!!equipment.isPortableTransducer && (
                          <TableCell sx={{ py: 0.5 }}>
                            {cal.correctionData && cal.correctionData.length > 0
                              ? <Tooltip title={cal.correctionData.map(r => `${r.psi} PSI: +${r.offsetNm} NM`).join(', ')}>
                                  <Typography variant="caption" color="primary" sx={{ cursor: 'help' }}>
                                    {cal.correctionData.length} offsets
                                  </Typography>
                                </Tooltip>
                              : <Typography variant="caption" color="text.secondary">None</Typography>
                            }
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 0.5 }}>
                          {cal.proxyUrl
                            ? <IconButton size="small" onClick={async () => {
                                const r = await fetch(baseUrl + cal.proxyUrl, { headers: authHeaders() });
                                const blob = await r.blob();
                                window.open(URL.createObjectURL(blob), '_blank');
                              }}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            : <Typography variant="caption" color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }} align="right">
                          <IconButton size="small" onClick={() => handleDeleteCal(cal.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <CalRecordModal
        open={calModalOpen}
        onClose={() => setCalModalOpen(false)}
        equipmentId={equipment.id}
        isPortableTransducer={!!equipment.isPortableTransducer}
        onSaved={() => { setCalModalOpen(false); loadCals(); onCalAdded(); }}
      />
    </>
  );
}

// ── Settings tab ─────────────────────────────────────────────────────────────

function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(baseUrl + 'workshop/settings', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setSettings(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const set = (key) => (e) => setSettings(s => ({ ...s, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    await fetch(baseUrl + 'workshop/settings', {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <CircularProgress sx={{ mt: 3 }} />;

  return (
    <Box sx={{ maxWidth: 800 }}>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Settings saved.</Alert>}

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Certificate Layout</Typography>
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Certificate Layout</InputLabel>
          <Select
            value={settings['certificateLayout'] || 'modern'}
            label="Certificate Layout"
            onChange={set('certificateLayout')}
          >
            <MenuItem value="modern">Modern (clean, recommended)</MenuItem>
            <MenuItem value="legacy">Legacy (traditional spreadsheet style)</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Switch to Legacy to match the original spreadsheet certificate format. All future certs use this setting — existing PDFs are unaffected.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Certificate Boilerplate Text</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These statements appear on each certificate type. Uncertainty values are also pulled from the selected test equipment's calibration record.
      </Typography>

      {BOILERPLATE_KEYS.map(({ key, label }) => (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
          <TextField
            size="small" fullWidth multiline rows={3}
            value={settings[key] || ''}
            onChange={set(key)}
            sx={{ mt: 0.5 }}
          />
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>Pay Period Settings</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pay Period Type</InputLabel>
          <Select value={settings['payPeriodType'] || 'FORTNIGHTLY'} label="Pay Period Type" onChange={set('payPeriodType')}>
            <MenuItem value="WEEKLY">Weekly</MenuItem>
            <MenuItem value="FORTNIGHTLY">Fortnightly</MenuItem>
            <MenuItem value="MONTHLY">Monthly</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small" label="Pay Period Start Date" type="date"
          value={settings['payPeriodStartDate'] || ''}
          onChange={set('payPeriodStartDate')}
          InputLabelProps={{ shrink: true }}
          helperText="First day of a known pay period — used to calculate current period"
        />
      </Box>

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? <CircularProgress size={20} /> : 'Save Settings'}
      </Button>
    </Box>
  );
}

// ── Cal Types tab ─────────────────────────────────────────────────────────────

const CAL_TYPES = [
  { key: 'HYD_TW', label: 'Hyd TW' }, { key: 'BATT_TW', label: 'Batt TW' },
  { key: 'HYD_PRESSURE', label: 'Hyd Press' }, { key: 'PNU', label: 'Pneumatic' },
  { key: 'GAUGE', label: 'Gauge' }, { key: 'DIAL', label: 'Dial' },
  { key: 'HOSE_MDG41', label: 'Hose MDG41' }, { key: 'HOSE_JACK', label: 'Hose/Jack' },
];

function CalTypeMappingTab({ assetTypes, calTypeMap, setCalTypeMap }) {
  const isMapped = (assetType, calType) => calTypeMap.some(m => m.assetType === assetType && m.calType === calType);

  const toggle = async (assetType, calType) => {
    const existing = calTypeMap.find(m => m.assetType === assetType && m.calType === calType);
    if (existing) {
      await fetch(baseUrl + 'workshop/settings/cal-type-map/' + existing.id, { method: 'DELETE', headers: authHeaders() });
      setCalTypeMap(prev => prev.filter(m => m.id !== existing.id));
    } else {
      const res = await fetch(baseUrl + 'workshop/settings/cal-type-map', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetType, calType }),
      });
      if (res.ok) {
        const rows = await fetch(baseUrl + 'workshop/settings/cal-type-map', { headers: authHeaders() }).then(r => r.json());
        setCalTypeMap(Array.isArray(rows) ? rows : []);
      }
    }
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tick which calibration types are available for each asset type. If none are ticked, calibration is disabled for that type.
      </Typography>
      <Table size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Asset Type</TableCell>
            {CAL_TYPES.map(ct => <TableCell key={ct.key} align="center" sx={{ fontWeight: 600, fontSize: 11 }}>{ct.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {assetTypes.map(at => (
            <TableRow key={at.value} hover>
              <TableCell sx={{ fontWeight: 600 }}>{at.label}</TableCell>
              {CAL_TYPES.map(ct => (
                <TableCell key={ct.key} align="center" padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isMapped(at.value, ct.key)}
                    onChange={() => toggle(at.value, ct.key)}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
          {assetTypes.length === 0 && (
            <TableRow><TableCell colSpan={CAL_TYPES.length + 1}><Typography color="text.secondary">No asset types configured.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WorkshopSettings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipModalOpen, setEquipModalOpen] = useState(false);
  const [editEquipment, setEditEquipment] = useState(null);

  const [assetTypes, setAssetTypes] = useState([]);
  const [calTypeMap, setCalTypeMap] = useState([]);
  const [mappingLoaded, setMappingLoaded] = useState(false);

  const loadMappings = async () => {
    if (mappingLoaded) return;
    const [at, cm] = await Promise.all([
      fetch(baseUrl + 'asset/typeAc/', { headers: authHeaders() }).then(r => r.json()),
      fetch(baseUrl + 'workshop/settings/cal-type-map', { headers: authHeaders() }).then(r => r.json()),
    ]);
    setAssetTypes(Array.isArray(at) ? at : []);
    setCalTypeMap(Array.isArray(cm) ? cm : []);
    setMappingLoaded(true);
  };

  const loadEquipment = () => {
    setLoading(true);
    fetch(baseUrl + 'workshop/settings/equipment', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setEquipment(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadEquipment(); }, []);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this equipment? It will no longer be selectable for calibration entry.')) return;
    await fetch(baseUrl + 'workshop/settings/equipment/' + id, { method: 'DELETE', headers: authHeaders() });
    loadEquipment();
  };

  const handleEdit = (equip) => { setEditEquipment(equip); setEquipModalOpen(true); };
  const handleAdd = () => { setEditEquipment(null); setEquipModalOpen(true); };

  const overdueCount = equipment.filter(e =>
    e.isActive && e.latestCertDueDate && dayjs(e.latestCertDueDate).isBefore(dayjs(), 'day')
  ).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/workshop')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Workshop Settings</Typography>
      </Box>

      {overdueCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {overdueCount} piece{overdueCount > 1 ? 's' : ''} of test equipment {overdueCount > 1 ? 'have' : 'has'} calibration overdue.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 2) loadMappings(); }} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Test Equipment" />
        <Tab label="Settings" />
        <Tab label="Cal Types" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              Add Equipment
            </Button>
          </Box>

          {loading
            ? <CircularProgress />
            : equipment.length === 0
              ? <Typography color="text.secondary">No test equipment registered.</Typography>
              : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name / Serial</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Latest Cal</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {equipment.map(e => (
                      <EquipmentRow
                        key={e.id}
                        equipment={e}
                        onEdit={handleEdit}
                        onDeactivate={handleDeactivate}
                        onCalAdded={loadEquipment}
                      />
                    ))}
                  </TableBody>
                </Table>
              )
          }
        </Box>
      )}

      {tab === 1 && <SettingsTab />}

      {tab === 2 && (
        <CalTypeMappingTab
          assetTypes={assetTypes}
          calTypeMap={calTypeMap}
          setCalTypeMap={setCalTypeMap}
        />
      )}

      <EquipmentModal
        open={equipModalOpen}
        onClose={() => setEquipModalOpen(false)}
        equipment={editEquipment}
        onSaved={() => { setEquipModalOpen(false); loadEquipment(); }}
      />
    </Box>
  );
}
